import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";

export interface Company {
  id: number;
  name: string;
  slug: string;
  status: string;
  plan: string;
  balance: number;
}

export interface CurrentUser {
  id: number;
  email: string;
  role: string;
}

interface CompanyContextValue {
  company: Company | null;
  companies: Company[];
  isLoading: boolean;
  currentUser: CurrentUser | null;
  switchCompany: (id: number) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextValue>({
  company: null,
  companies: [],
  isLoading: true,
  currentUser: null,
  switchCompany: async () => {},
});

interface CompaniesResponse {
  companies: Company[];
  current_company_id: number | null;
  current_user: CurrentUser | null;
}

export function CompanyProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [currentId, setCurrentId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["companies"],
    queryFn: () => apiFetch<CompaniesResponse>("/api/companies"),
  });

  useEffect(() => {
    if (data?.current_company_id && !currentId) {
      setCurrentId(data.current_company_id);
    }
  }, [data, currentId]);

  const switchCompany = useCallback(async (id: number) => {
    await apiFetch(`/api/companies/${id}/switch`, { method: "POST" });
    setCurrentId(id);
    queryClient.invalidateQueries();
  }, [queryClient]);

  const company = data?.companies.find((c) => c.id === currentId) ?? data?.companies[0] ?? null;

  return (
    <CompanyContext.Provider value={{
      company,
      companies: data?.companies ?? [],
      isLoading,
      currentUser: data?.current_user ?? null,
      switchCompany,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  return useContext(CompanyContext);
}
